import { rmaNavigationState } from '@/entities/rma';
import { useMergeState } from '@/shared/lib/customHooks';
import { useRef } from 'react';
import type { ClickedDevice, DevicesMaintenanceTableState } from './devicesMaintenanceTable.types';

/**
 * Holds the state of the RMA devices-in-maintenance table: the debounced search text, the model filter and the device information drawer. Seeds the search value and model from the persisted rmaNavigationState and exposes handlers to toggle the drawer for a clicked device and to update the search inputs.
 *
 * @returns The table state with the drawer toggle and search change handlers.
 */
const useDevicesMaintenanceTable = () => {
  const [state, setState] = useMergeState<DevicesMaintenanceTableState>({
    clickedDevice: null,
    isVisibleDeviceInfoDrawer: false,
    searchValue: rmaNavigationState.maintenance.search.searchValue || '',
    model: rmaNavigationState.maintenance.select.model,
  });

  const updateSearchValueTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Opens or closes the device information drawer of the maintenance table. Stores the clicked device and inverts isVisibleDeviceInfoDrawer in one state merge.
   *
   * @param clickedDevice - Device whose information the drawer should show.
   * @returns Nothing.
   */
  const toggleDisplayDrawer = (clickedDevice?: ClickedDevice) => {
    setState({
      clickedDevice,
      isVisibleDeviceInfoDrawer: !state.isVisibleDeviceInfoDrawer,
    });
  };

  /**
   * Updates a search input of the maintenance device table while debouncing the value used for querying. Writes the raw value under its own field immediately and schedules searchValue 500 ms later, cancelling any pending timer first.
   *
   * @param name - State field of the input being edited.
   * @param value - Text entered in that input.
   * @returns Nothing.
   */
  const onChangeSearchValue = (name: string, value: string) => {
    if (updateSearchValueTimeout.current) {
      clearTimeout(updateSearchValueTimeout.current);
      updateSearchValueTimeout.current = null;
    }
    updateSearchValueTimeout.current = setTimeout(() => {
      setState({ searchValue: value });
    }, 500);
    setState({ [name]: value });
  };

  return {
    state,
    toggleDisplayDrawer,
    onChangeSearchValue,
  };
};

export { useDevicesMaintenanceTable };
